import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Form, Alert, Modal, Card, Row, Col } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { addResellerToWaitlist } from '@/api/reseller';
import { notification } from 'antd';
import { useDispatch } from 'react-redux';
import {
	setOnboardingInfo,
	ResellerOnboardingState,
	setStep,
	unsetOnboardingInfo,
} from '@/store/resellerOnboardingSteps/slice';
import posthog from 'posthog-js';
import { countries } from 'countries-list';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { createHubspotContact, getHubspotContact } from '@/api/hubspot';
import { landingPageURL } from 'src/config';
import {
	ClientType,
	CompanyType,
	PremiumPartnershipCommitment,
} from '../../enums/ELeadQualification';
import { useClarity } from '../../Hooks/useClarity';
const { TextArea } = Input;
import { createCheckoutSession } from '@/api/customOffer';

type CountryOption = {
	value: string;
	label: string;
};

const countryOptions: CountryOption[] = Object.entries(countries)
	.map(([code, country]) => ({
		value: code,
		label: country.name,
	}))
	.sort((a, b) => a.label.localeCompare(b.label));

const Step2: React.FC<{ customOffer: boolean; encodedData: string }> = ({
	customOffer,
	encodedData,
}) => {
	// Get saved data from localStorage when component mounts
	const savedData = (LocalStorageUtility.getLocalData(LocalStorageKeys.RESELLER_ONBOARDING) ||
		{}) as ResellerOnboardingState;

	const [selectedLocation, setSelectedLocation] = useState<string | undefined>(savedData.country);
	const [currentStep, setCurrentStep] = useState(1);
	const [form] = Form.useForm();
	const [desiredCountry, setDesiredCountry] = useState<string>(savedData.desiredCountry || '');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const dispatch = useDispatch();
	const clarity = useClarity();

	const addToWaitlistMutation = useMutation({
		mutationKey: ['addResellerToWaitlist'],
		mutationFn: addResellerToWaitlist,
		onSuccess() {
			notification.success({
				message: 'Successfully joined waitlist',
				description: `We'll notify you when we launch in ${desiredCountry ? countryOptions.find((c) => c.value === desiredCountry)?.label : 'your region'}.`,
			});
			dispatch(setStep(2));
		},
		onError(error: any) {
			// Check if error is due to already being in waitlist
			if (error.message?.toLowerCase().includes('email already in the waitlist')) {
				notification.info({
					message: 'Already in Waitlist',
					description: `You're already on our waitlist! We'll notify you when we launch in ${desiredCountry ? countryOptions.find((c) => c.value === desiredCountry)?.label : 'your region'}.`,
				});
			} else {
				notification.error({
					message: error.message || 'Error joining waitlist',
				});
			}
		},
	});

	const customOfferCheckoutSessionMutation = useMutation({
		mutationKey: ['customOfferCheckoutSession'],
		mutationFn: createCheckoutSession,
		onSuccess(data) {
			if (data.success) {
				window.location.href = data.checkoutURL;
			} else {
				notification.error({
					message: 'Error creating checkout session',
				});
			}
		},
		onError(error: any) {
			notification.error({
				message: error.message || 'Error creating checkout session',
			});
		},
	});

	const createHubspotContactMutation = useMutation({
		mutationKey: ['createHubspotContact'],
		mutationFn: createHubspotContact,
		onSuccess() {
			// HubSpot contact created successfully
			notification.success({
				message: 'Contact information saved',
				description: 'Your information has been saved to our system.',
			});
		},
		onError() {
			notification.error({
				message: 'Failed to save contact information',
				description:
					'There was an issue saving your information. Please try again or contact support if the problem persists.',
				duration: 5,
			});
		},
	});

	const getHubspotContactMutation = useMutation({
		mutationKey: ['getHubspotContactData'],
		mutationFn: getHubspotContact,
	});

	// Set initial form values from localStorage
	useEffect(() => {
		if (savedData && !customOffer) {
			// Set the form values
			form.setFieldsValue({
				country: savedData.country,
				email: savedData.email,
				firstName: savedData.firstName,
				lastName: savedData.lastName,
				businessName: savedData.businessName,
				clientsCount: savedData.clientsCount,
				businessType: savedData.businessType,
				businessRevenue: savedData.businessRevenue,
				businessEmployeeSize: savedData.businessEmployeeSize,
				businessWebsite: savedData.businessWebsite,
				planToSell: savedData.planToSell,
				password: savedData.password,
				desiredCountry: savedData.desiredCountry,
				testedReceptionist: savedData.testedReceptionist,
				premiumPartnershipCommitment: savedData.premiumPartnershipCommitment,
			});
		}
		if (customOffer) {
			dispatch(setStep(1));
			unsetOnboardingInfo(); // Clear onboarding info if custom offer
		}
	}, []);

	const handleLocationChange = (value: string) => {
		setSelectedLocation(value);
		if (value && value !== 'Not listed') {
			setCurrentStep(2); // Move to company name and email row for valid locations
		} else if (value === 'Not listed') {
			setCurrentStep(2); // Move to email for not listed
		}
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value);
		if (isValid && currentStep === 2) {
			setCurrentStep(3); // Move to premium partnership question after valid email
		}
	};

	const validateWorkEmail = (_: any, value: string) => {
		const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
		const domain = value.split('@')[1]?.toLowerCase();

		if (!value) {
			return Promise.reject('Please enter your work email');
		}

		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
			return Promise.reject('Please enter a valid work email');
		}

		if (personalDomains.includes(domain)) {
			return Promise.reject('Please use your work email address');
		}

		return Promise.resolve();
	};

	const isQualified = (values: any) => {
		const { clientsCount, businessType, premiumPartnershipCommitment } = values;

		// Check if lead is unqualified based on new criteria
		const isUnqualified =
			clientsCount === ClientType.SMALL ||
			businessType === CompanyType.SOLOPRENEUR ||
			premiumPartnershipCommitment === PremiumPartnershipCommitment.NO;

		return {
			isUnqualified,
			isQualified: !isUnqualified,
		};
	};

	const handleSubmit = async (values: any) => {
		const {
			email,
			password,
			businessName,
			country,
			firstName,
			lastName,
			desiredCountry,
			testedReceptionist,
			clientsCount,
			businessType,
			businessRevenue,
			businessEmployeeSize,
			businessWebsite,
			planToSell,
			premiumPartnershipCommitment,
		} = values;

		// Track in PostHog
		posthog.identify(email, {
			email,
			password,
			businessName,
			country,
			firstName,
			lastName,
			desiredCountry,
			testedReceptionist,
			clientsCount,
			businessType,
			businessRevenue,
			businessEmployeeSize,
			businessWebsite,
			planToSell,
			premiumPartnershipCommitment,
		});

		// Track in Clarity
		clarity('identify', email);
		clarity('set', {
			email,
			password,
			businessName,
			country,
			firstName,
			lastName,
			desiredCountry,
			testedReceptionist,
			clientsCount,
			businessType,
			businessRevenue,
			businessEmployeeSize,
			businessWebsite,
			planToSell,
			premiumPartnershipCommitment,
		});

		if (customOffer) {
			customOfferCheckoutSessionMutation.mutate({
				encodedData,
				userData: {
					username: email.toLowerCase(),
					emailAddress: email.toLowerCase(),
					businessName: businessName || 'na',
					clientLocation: country || 'na',
					password: password || '',
					type: 'Reseller',
					firstName,
					lastName,
					clientsCount: clientsCount || 'Not Applicable',
					businessType: businessType || 'Not Applicable',
					businessRevenue: businessRevenue || 'Not Applicable',
					businessEmployeeSize: businessEmployeeSize || 'Not Applicable',
					businessWebsite: businessWebsite || 'Not Applicable',
					planToSell: planToSell || 'Not Applicable',
					testedReceptionist: testedReceptionist || 'Not Applicable',
				},
			});

			return;
		}

		if (country === 'Not listed') {
			addToWaitlistMutation.mutate({
				emailAddress: email.toLowerCase(),
				countryCode: desiredCountry,
				country: countryOptions.find((c) => c.value === desiredCountry)!.label,
			});
			return;
		}

		try {
			setIsSubmitting(true);

			// Save form data to Redux
			dispatch(
				setOnboardingInfo({
					email: email.toLowerCase(),
					businessName,
					country,
					password,
					firstName,
					lastName,
					clientsCount,
					businessType,
					businessRevenue,
					businessEmployeeSize,
					businessWebsite,
					planToSell,
					testedReceptionist,
					premiumPartnershipCommitment,
				}),
			);

			// Check if contact exists in HubSpot first
			try {
				const hubspotData = await getHubspotContactMutation.mutateAsync(email.toLowerCase());
				if (hubspotData && hubspotData.found) {
					notification.info({
						message: 'You have already registered',
						description: 'Our team will reach out to you soon.',
						duration: 5,
					});
					return; // Do not proceed further if contact exists in HubSpot
				}
				// If found: false, continue with the flow - this is expected behavior
			} catch (_hubspotCheckError) {
				notification.error({
					message: 'Failed to check existing contact',
					description: 'An unexpected error occurred while checking our system. Please try again.',
					duration: 5,
				});
				return;
			}

			// Create HubSpot contact for ALL leads (both qualified and unqualified)
			try {
				await createHubspotContactMutation.mutateAsync({
					email: email.toLowerCase(),
					businessName,
					country,
					firstName,
					lastName,
					password,
					testedReceptionist,
					clientsCount,
					businessType,
					businessRevenue,
					businessEmployeeSize,
					businessWebsite,
					planToSell,
					premiumPartnershipCommitment,
				});

				// Show success message for contact creation
				notification.success({
					message: 'Information saved successfully',
					description: 'Your details have been saved to our system.',
					duration: 3,
				});
			} catch (hubspotError) {
				console.error('Error creating HubSpot contact:', hubspotError);
				notification.error({
					message: 'Failed to save contact information',
					description:
						'There was an issue saving your information. Please try again or contact support.',
					duration: 5,
				});
				return;
			}

			// Check qualification after HubSpot contact is created
			const qualification = isQualified(values);

			if (qualification.isUnqualified) {
				// Unqualified leads - set step to 2 (affiliate program)
				notification.info({
					message: 'Thank you for your interest!',
					description:
						"We're reviewing your application and will reach out to discuss the best path forward.",
					duration: 4,
				});
				dispatch(setStep(2)); // This will set step to 2
				return;
			}

			// Qualified leads - proceed to step 3
			posthog.capture('Reseller_Setup_Details_Submitted');
			clarity('event', 'Reseller_Setup_Details_Submitted');
			notification.success({
				message: 'Great! You qualify for our program',
				description: "Let's proceed with your registration.",
				duration: 3,
			});
			dispatch(setStep(3)); // This will set step to 3
		} catch (error) {
			notification.error({
				message: 'Failed to submit form',
				description: `An unexpected error occurred. Please try again. Error Details: ${error.message}`,
				duration: 5,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const isNotListed = selectedLocation === 'Not listed';

	// Update form field validation when location changes
	React.useEffect(() => {
		// Reset fields when changing location type
		if (isNotListed) {
			form.setFieldsValue({
				businessName: undefined,
				clientsCount: undefined,
				businessType: undefined,
				businessRevenue: undefined,
				businessEmployeeSize: undefined,
				businessWebsite: undefined,
				planToSell: undefined,
				premiumPartnershipCommitment: undefined,
			});
		}
	}, [isNotListed, form]);

	React.useEffect(() => {
		posthog.capture('Reseller_Setup_Details_Viewed');
		clarity('event', 'Reseller_Setup_Details_Viewed');
	}, []);

	const getButtonText = () => (isNotListed ? 'Join Waitlist' : 'Submit');

	return (
		<>
			<div className="md:h-screen h-auto overflow-y-auto w-full overflow-x-hidden flex flex-col justify-center ">
				<div className="p-6">
					<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm p-1 overflow-hidden mt-5">
						<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />
						<h2 className="text-2xl font-bold mb-3 leading-tight text-center">
							Premium White Label Program
						</h2>
						<p className="text-center text-gray-500 text-sm">
							Tell us about your business and let's explore how we can work together
						</p>

						<Form layout="vertical" onFinish={handleSubmit} className="w-full mx-auto" form={form}>
							<div className="mb-6">
								<Form.Item
									label="Where are your clients located?"
									name="country"
									rules={[{ required: true, message: 'Please select a location' }]}
								>
									<Select
										placeholder="Select a country"
										size="large"
										className="w-full"
										options={[
											{ value: 'US', label: 'United States' },
											{ value: 'UK', label: 'United Kingdom' },
											{ value: 'AU', label: 'Australia' },
											{ value: 'CA', label: 'Canada' },
											{ value: 'AT', label: 'Austria' },
											{ value: 'FR', label: 'France' },
											{ value: 'DK', label: 'Denmark' },
											{ value: 'CH', label: 'Switzerland' },
											{ value: 'NL', label: 'Netherlands' },
											{ value: 'FI', label: 'Finland' },
											{ value: 'NZ', label: 'New Zealand' },
											{ value: 'DE', label: 'Germany' },
											{ value: 'ES', label: 'Spain' },
											{ value: 'Not listed', label: 'Not listed' },
										]}
										onChange={handleLocationChange}
									/>
								</Form.Item>
							</div>

							{currentStep >= 2 && isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Which country would you like to sell in?"
												name="desiredCountry"
												rules={[{ required: true, message: 'Please select your desired country' }]}
											>
												<Select
													showSearch
													placeholder="Select your desired country"
													size="large"
													className="w-full"
													options={countryOptions}
													onChange={(value) => setDesiredCountry(value)}
													filterOption={(input, option) =>
														(option?.label.toLowerCase().indexOf(input.toLowerCase()) || 0) >= 0
													}
												/>
											</Form.Item>
											{/* <Alert
													message="We're not available in your region yet, but we're expanding quickly!"
													description={`We'll notify you when we launch in ${desiredCountry ? countryOptions.find((c) => c.value === desiredCountry)?.label : 'your region'}.`}
													type="info"
													showIcon
													className="mt-2"
												/> */}
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Work Email"
												name="email"
												rules={[
													{ required: true, message: 'Please enter your work email' },
													{ validator: validateWorkEmail },
												]}
											>
												<Input
													placeholder="you@company.com"
													size="large"
													className="w-full"
													prefix={<span className="text-gray-400">📧</span>}
													onChange={handleEmailChange}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{currentStep >= 2 && !isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Company Name"
												name="businessName"
												rules={[
													{ required: !isNotListed, message: 'Please enter your company name' },
												]}
											>
												<Input
													placeholder="Your Agency or Company"
													size="large"
													className="w-full"
													onChange={() => {
														const businessName = form.getFieldValue('businessName');
														const email = form.getFieldValue('email');
														if (businessName && email && currentStep === 2) {
															setCurrentStep(3); // Move to premium partnership question after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Work Email"
												name="email"
												rules={[
													{ required: true, message: 'Please enter your work email' },
													{ validator: validateWorkEmail },
												]}
											>
												<Input
													placeholder="you@company.com"
													size="large"
													className="w-full"
													prefix={<span className="text-gray-400">📧</span>}
													onChange={(e) => {
														handleEmailChange(e);
														const businessName = form.getFieldValue('businessName');
														const email = e.target.value;
														if (businessName && email && currentStep === 2) {
															setCurrentStep(3); // Move to premium partnership question after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{currentStep >= 3 && !isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="This is a premium partnership that requires commitment and is $500+ per month. Is that in budget?"
												name="premiumPartnershipCommitment"
												rules={[
													{ required: !isNotListed, message: 'Please select a valid option' },
												]}
											>
												<Select
													placeholder="Select an option"
													size="large"
													className="w-full"
													options={[
														{ value: 'yes', label: 'Yes' },
														{ value: 'no', label: 'No' },
													]}
													onChange={() => {
														const value = form.getFieldValue('premiumPartnershipCommitment');
														const clientsCount = form.getFieldValue('clientsCount');
														if (value && clientsCount && currentStep === 3) {
															setCurrentStep(4); // Move to business type after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="How many clients are you bringing into the program?"
												name="clientsCount"
												rules={[
													{ required: !isNotListed, message: 'Please select a valid option' },
												]}
											>
												<Select
													placeholder="Select client count"
													size="large"
													className="w-full"
													options={[
														{ value: '0-2', label: '0-2' },
														{ value: '3-15', label: '3-15' },
														{ value: '16-50', label: '16-50' },
														{ value: '51-100', label: '51-100' },
														{ value: '101+', label: '101+' },
													]}
													onChange={() => {
														const value = form.getFieldValue('clientsCount');
														const premiumPartnership = form.getFieldValue(
															'premiumPartnershipCommitment',
														);
														if (value && premiumPartnership && currentStep === 3) {
															setCurrentStep(4); // Move to business type after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{currentStep >= 4 && !isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="What describes you best?"
												name="businessType"
												rules={[
													{ required: !isNotListed, message: 'Please select a valid option' },
												]}
											>
												<Select
													placeholder="Select an option which best describes you"
													size="large"
													className="w-full"
													options={[
														{ value: 'agency/consultant', label: 'Agency/Consultant' },
														{ value: 'solopreneur', label: 'Solopreneur' },
														{ value: 'tech_company', label: 'Tech company adding services' },
														{ value: 'VoIP', label: 'VoIP company adding services' },
														{ value: 'other', label: 'Other' },
													]}
													onChange={() => {
														const value = form.getFieldValue('businessType');
														const businessRevenue = form.getFieldValue('businessRevenue');
														if (value && businessRevenue && currentStep === 4) {
															setCurrentStep(5); // Move to team size after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="What's your company revenue?"
												name="businessRevenue"
												rules={[
													{ required: !isNotListed, message: 'Please select a valid option' },
												]}
											>
												<Select
													placeholder="Select your revenue"
													size="large"
													className="w-full"
													options={[
														{ value: '$0-$10k', label: '$0k-$10k' },
														{ value: '$11-$100k', label: '$11k-$100k' },
														{ value: '$100k-$250k', label: '$100k-$250k' },
														{ value: '$250k-$750k', label: '$250k-$750k' },
														{ value: '750k-2mil', label: '$750k-$2mil' },
														{ value: '$2mil+', label: '$2mil+' },
													]}
													onChange={() => {
														const value = form.getFieldValue('businessRevenue');
														const businessType = form.getFieldValue('businessType');
														if (value && businessType && currentStep === 4) {
															setCurrentStep(5); // Move to team size after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{currentStep >= 5 && !isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="What is your team size?"
												name="businessEmployeeSize"
												rules={[
													{ required: !isNotListed, message: 'Please select a valid option' },
												]}
											>
												<Select
													placeholder="Select your team size"
													size="large"
													className="w-full"
													options={[
														{ value: '1-5', label: '1-5' },
														{ value: '6-15', label: '6-15' },
														{ value: '16-25', label: '16-25' },
														{ value: '25+', label: '25+' },
													]}
													onChange={() => {
														const value = form.getFieldValue('businessEmployeeSize');
														const businessWebsite = form.getFieldValue('businessWebsite');
														if (value && businessWebsite && currentStep === 5) {
															setCurrentStep(6); // Move to testing question after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Your website"
												name="businessWebsite"
												rules={[{ required: !isNotListed, message: 'Please enter your website' }]}
											>
												<Input
													placeholder="https://yourwebsite.com"
													size="large"
													className="w-full"
													onChange={() => {
														const value = form.getFieldValue('businessWebsite');
														const businessEmployeeSize = form.getFieldValue('businessEmployeeSize');
														if (value && businessEmployeeSize && currentStep === 5) {
															setCurrentStep(6); // Move to testing question after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{currentStep >= 6 && !isNotListed && (
								<Row gutter={16}>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item
												label="Have you tested our AI receptionist?"
												name="testedReceptionist"
												rules={[{ required: !isNotListed, message: 'Please select an option' }]}
											>
												<Select
													placeholder="Select an option"
													size="large"
													className="w-full"
													options={[
														{ value: 'yes', label: 'Yes' },
														{ value: 'no', label: 'No' },
													]}
													onChange={() => {
														const value = form.getFieldValue('testedReceptionist');
														const planToSell = form.getFieldValue('planToSell');
														if (value && planToSell && currentStep === 6) {
															setCurrentStep(7); // Move to name fields after both fields are filled
														}
													}}
												/>
											</Form.Item>
											<Alert
												message="Testing our AI receptionist ensures you can confidently deliver quality solutions to your clients."
												type="info"
												showIcon
												className="mt-2"
											/>
										</div>
									</Col>
									<Col md={12} xs={24}>
										<div className="mb-6">
											<Form.Item label="How do you plan to sell our AI?" name="planToSell">
												<TextArea
													placeholder="Please describe how you plan to sell our AI"
													size="large"
													className="w-full"
													rows={5}
													onChange={() => {
														const value = form.getFieldValue('planToSell');
														const testedReceptionist = form.getFieldValue('testedReceptionist');
														if (value && testedReceptionist && currentStep === 6) {
															setCurrentStep(7); // Move to name fields after both fields are filled
														}
													}}
												/>
											</Form.Item>
										</div>
									</Col>
								</Row>
							)}

							{/* Step 7 - Name fields and password */}
							{!isNotListed && currentStep >= 7 && (
								<>
									<div className="mb-6 flex gap-4">
										<Form.Item
											label="First Name"
											name="firstName"
											className="flex-1"
											rules={[{ required: true, message: 'Please enter your first name' }]}
										>
											<Input
												placeholder="First Name"
												size="large"
												onChange={() => {
													const firstName = form.getFieldValue('firstName');
													const lastName = form.getFieldValue('lastName');
													if (firstName && lastName && currentStep === 7) {
														setCurrentStep(8); // Move to password after both names are filled
													}
												}}
											/>
										</Form.Item>
										<Form.Item
											label="Last Name"
											name="lastName"
											className="flex-1"
											rules={[{ required: true, message: 'Please enter your last name' }]}
										>
											<Input
												placeholder="Last Name"
												size="large"
												onChange={() => {
													const firstName = form.getFieldValue('firstName');
													const lastName = form.getFieldValue('lastName');
													if (firstName && lastName && currentStep === 7) {
														setCurrentStep(8); // Move to password after both names are filled
													}
												}}
											/>
										</Form.Item>
									</div>
									{currentStep >= 8 && (
										<div className="mb-6">
											<Form.Item
												label="Choose a password"
												name="password"
												rules={[{ required: true, message: 'Please enter your password' }]}
											>
												<Input.Password
													placeholder="Enter your password"
													size="large"
													className="w-full"
												/>
											</Form.Item>
										</div>
									)}
								</>
							)}

							<Button
								className="h-12 w-full text-base bg-blue-500 hover:bg-blue-600 border-none rounded-md"
								type="primary"
								htmlType="submit"
								loading={
									isSubmitting ||
									addToWaitlistMutation.isPending ||
									customOfferCheckoutSessionMutation.isPending ||
									createHubspotContactMutation.isPending ||
									getHubspotContactMutation.isPending
								}
							>
								<span>{getButtonText()}</span>
							</Button>
						</Form>
					</Card>
				</div>
			</div>

			{/* Unqualified Modal */}
			<Modal
				title={
					<div className="text-center">
						<h2 className="text-2xl font-bold text-gray-800 mb-2">Thanks for Your Interest!</h2>
						<p className="text-gray-600">We appreciate you considering our reseller program.</p>
					</div>
				}
				open={false}
				onCancel={() => {
					form.resetFields();
					setCurrentStep(1);
					window.location.href = landingPageURL;
				}}
				footer={null}
				width={600}
				centered
			>
				<div className="p-6">
					<div className="text-center mb-6">
						<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
							<svg
								className="w-8 h-8 text-blue-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<h3 className="text-xl font-semibold text-gray-800 mb-2">
							We've Received Your Information
						</h3>
						<p className="text-gray-600 mb-4">
							Our team will review your application and reach out to you within the next week to
							discuss potential opportunities.
						</p>
					</div>

					<div className="bg-gray-50 rounded-lg p-4 mb-6">
						<h4 className="font-semibold text-gray-800 mb-2">What's Next?</h4>
						<ul className="space-y-2 text-gray-600">
							<li className="flex items-start">
								<span className="mr-2">•</span>
								<span>Our team will review your application</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2">•</span>
								<span>We'll contact you to discuss your business needs</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2">•</span>
								<span>Together, we'll explore the best path forward</span>
							</li>
						</ul>
					</div>

					<div className="text-center">
						<Button
							type="primary"
							size="large"
							onClick={() => {
								form.resetFields();
								setCurrentStep(1);
								window.location.href = landingPageURL;
							}}
							className="w-full bg-blue-500 hover:bg-blue-600"
						>
							Close
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default Step2;
